import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Collapse, CircularProgress, Link } from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { schoonMailTekst, splitsMail } from '../../utils/mailTekst';
import toast from 'react-hot-toast';
import { statusVoorGroep } from './useMailNotities';
import FeedbackUitMailDialog from './FeedbackUitMailDialog';

// Design tokens — spiegel van ProjectOverview zodat de tab in stijl blijft.
const T = {
  primary: '#526075', tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 20, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Taken + deliverables van het project, voor de Feedback-dialoog (kies de v2/DEF-taak).
const ProjectTakenContext = createContext({ tasks: [], deliverables: [] });

// "Als notitie" loopt via de server (useMailNotities in ProjectDataContext): de status per
// gesprek komt uit de boekhouding, niet uit lokale state — na heropenen staat er dus gewoon
// "✓ Notitie", en een tweede klik maakt niets dubbel (9856, 23 okt 2026).
const MailNotitieContext = createContext(null);

const groepVanMail = (mail) => ({
  // eslint-disable-next-line no-use-before-define
  key: mail.conversationId || `onderwerp:${zonderPrefix(mail.onderwerp).toLowerCase()}`,
  berichten: [mail],
});

export function NotitieKnop({ groep, label, titel, stijl }) {
  const mn = useContext(MailNotitieContext);
  const [starten, setStarten] = useState(false);
  if (!mn) return null;
  const st = statusVoorGroep(groep, mn.stand);
  const bezig = starten || st.soort === 'bezig';
  const bewaar = async (e) => {
    e.stopPropagation();
    if (bezig) return;
    setStarten(true);
    try {
      await mn.start({ messageIds: groep.berichten.map((m) => m.id), gesprek: groep.key });
    } catch (err) {
      toast.error(`Opslaan als notitie mislukt: ${err.message || 'onbekende fout'} — probeer het opnieuw`);
    } finally {
      setStarten(false);
    }
  };
  const openNotitie = (e) => { e.stopPropagation(); mn.openNotitie?.(st.noteId); };
  // Eén los bericht dat nog niet in de gespreksnotitie staat: gewoon de knop (die zet hem erbij).
  const bewaard = st.noteId && !(groep.berichten.length === 1 && st.nieuw > 0);
  if (bezig) {
    return (
      <Box component="span" sx={{ ...stijl, cursor: 'default' }} title="Wordt op de server bewaard — je kunt dit venster sluiten">
        <CircularProgress size={11} sx={{ color: T.outline }} /> Bezig…
      </Box>
    );
  }
  if (bewaard) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" onClick={openNotitie} title="Open de notitie"
          sx={{ ...stijl, color: '#2e7d32' }}>
          <Icon name="check" size={15} /> Notitie{st.datum ? ` · ${formatDate(st.datum)}` : ''}
        </Box>
        {st.soort === 'fout' ? (
          <Box component="span" onClick={bewaar} title={st.fout} sx={{ ...stijl, color: '#a83836' }}>· bijwerken mislukt — opnieuw</Box>
        ) : st.nieuw > 0 && (
          <Box component="span" onClick={bewaar} title="Zet de nieuwe berichten erbij"
            sx={{ ...stijl, color: T.primary }}>
            · {st.nieuw} nieuw bericht{st.nieuw === 1 ? '' : 'en'} – bijwerken
          </Box>
        )}
      </Box>
    );
  }
  if (st.soort === 'fout') {
    return (
      <Box component="span" onClick={bewaar} title={st.fout} sx={{ ...stijl, color: '#a83836' }}>
        <Icon name="error" size={15} /> Mislukt — opnieuw
      </Box>
    );
  }
  return (
    <Box component="span" onClick={bewaar} title={titel} sx={stijl}>
      <Icon name="note_add" size={15} /> {label}
    </Box>
  );
}

// Eén mail-rij: uitklapbaar, laadt de volledige body pas bij openen.
function MailRow({ mail, projectId, projectNumber, onWeggeklikt, compact = false }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(null);
  const [loadingBody, setLoadingBody] = useState(false);
  const [wegBezig, setWegBezig] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { tasks, deliverables } = useContext(ProjectTakenContext);

  const haalBody = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/mail-body?messageId=${encodeURIComponent(mail.id)}`);
      const data = await leesJson(r, 'Mail laden');
      return String(data?.body || data?.text || mail.preview || '').trim() || mail.preview || '';
    } catch (e) {
      meldFout(e, 'Mail laden');
      return mail.preview || '';
    }
  }, [mail.id, mail.preview]);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && body === null && !loadingBody) {
      setLoadingBody(true);
      setBody(await haalBody());
      setLoadingBody(false);
    }
  }, [open, body, loadingBody, haalBody]);


  // Klantfeedback → punten bij een v2/DEF-taak. Eerst de volledige tekst, want de AI krijgt
  // alleen wat jij in de dialoog laat staan.
  const openFeedback = useCallback(async (e) => {
    e.stopPropagation();
    if (body === null) setBody(await haalBody());
    setFeedbackOpen(true);
  }, [body, haalBody]);

  const nietRelevant = useCallback(async (e) => {
    e.stopPropagation();
    if (wegBezig) return;
    setWegBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/correspondence-dismiss`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectNumber, messageId: mail.id }),
      });
      if (!r.ok) throw new Error('mislukt');
      onWeggeklikt?.(mail.id);
    } catch {
      setWegBezig(false);
    }
  }, [wegBezig, projectNumber, mail.id, onWeggeklikt]);

  const actieStijl = {
    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
    fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline,
    '&:hover': { color: T.primary, textDecoration: 'underline' },
  };

  return (
    <Box sx={{ bgcolor: T.surface, boxShadow: T.shadow, borderRadius: '0.75rem', border: `1px solid ${T.surfaceHigh}`, overflow: 'hidden' }}>
      <Box
        onClick={toggle}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, cursor: 'pointer',
          transition: 'background-color 0.12s ease',
          '&:hover': { bgcolor: T.surfaceLow },
        }}
      >
        <Box sx={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s', display: 'flex', mt: 0.25 }}>
          <Icon name="expand_more" size={20} sx={{ color: T.outlineVariant }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mail.hasAttachments && <Box component="span" title="Met bijlage — gaat mee als je een notitie maakt" sx={{ mr: 0.5 }}>📎</Box>}
            {mail.onderwerp || '(geen onderwerp)'}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Icon name="person" size={12} /> {mail.van || 'onbekend'}
          </Typography>
          {!open && mail.preview && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {mail.preview}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          {projectId && (
            <NotitieKnop groep={groepVanMail(mail)} label="Notitie" titel="Bewaar deze mail als projectnotitie" stijl={actieStijl} />
          )}
          {projectNumber && (
            <Box component="span" onClick={openFeedback} title="Zet de feedback uit deze mail als punten bij een v2- of DEF-taak"
              sx={actieStijl}>
              <Icon name="rate_review" size={15} /> Feedback
            </Box>
          )}
          {projectNumber && (
            <Box component="span" onClick={nietRelevant} title="Verberg deze suggestie (alleen voor jou)"
              sx={{ ...actieStijl, opacity: wegBezig ? 0.5 : 1 }}>
              <Icon name="visibility_off" size={15} /> Niet relevant
            </Box>
          )}
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, whiteSpace: 'nowrap' }}>
            {formatDate(mail.datum)}
          </Typography>
          {mail.webLink && (
            <Box
              component="a"
              href={mail.webLink}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Open in Outlook"
              sx={{ display: 'inline-flex', color: T.outlineVariant, '&:hover': { color: T.primary } }}
            >
              <Icon name="open_in_new" size={16} />
            </Box>
          )}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 0, borderTop: `1px solid ${T.surfaceLow}` }}>
          {loadingBody ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Mail laden…</Typography>
            </Box>
          ) : (
            <MailInhoud tekst={body || mail.preview || ''} onderwerp={mail.onderwerp} />
          )}
        </Box>
      </Collapse>
      {feedbackOpen && (
        <FeedbackUitMailDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} mail={mail}
          body={body || mail.preview || ''} projectNumber={projectNumber} tasks={tasks} deliverables={deliverables} />
      )}
    </Box>
  );
}

// Mailtekst leesbaar: eigen tekst met klikbare links, handtekening en eerdere berichten
// ingeklapt (zelfde splitsing als de mail-notities in Notities, 18 sep 2026).
const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;
function metLinks(tekst) {
  return String(tekst || '').split(URL_RE).map((deel, i) => (
    URL_RE.test(deel) && /^https?:\/\//.test(deel)
      ? <Link key={i} href={deel} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          sx={{ wordBreak: 'break-all', color: T.primary }}>{deel.length > 70 ? `${deel.slice(0, 64)}…` : deel}</Link>
      : <React.Fragment key={i}>{deel}</React.Fragment>
  ));
}
function Uitklap({ label, tekst }) {
  const [open, setOpen] = useState(false);
  if (!tekst) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Box component="span" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        sx={{ cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, '&:hover': { color: T.primary } }}>
        {open ? '▾' : '▸'} {label}
      </Box>
      {open && (
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, whiteSpace: 'pre-wrap', mt: 0.5, pl: 1.5, borderLeft: `2px solid ${T.surfaceHigh}`, fontSize: '0.8rem', lineHeight: 1.55 }}>
          {metLinks(tekst)}
        </Typography>
      )}
    </Box>
  );
}
function MailInhoud({ tekst, onderwerp }) {
  if (!tekst) return <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, mt: 1.5 }}>Geen inhoud beschikbaar.</Typography>;
  const gesplitst = splitsMail(tekst, { onderwerp });
  // Alleen een handtekening + oud bericht (bv. leeg doorgestuurd zonder FW in het onderwerp):
  // dan is het oude bericht de inhoud — open tonen, niet de rauwe tekst met plaatjes-URL's.
  const alleenCitaat = !gesplitst.eigen && !gesplitst.doorgestuurd && gesplitst.citaat;
  const { handtekening, doorgestuurd } = gesplitst;
  const eigen = alleenCitaat ? gesplitst.citaat : gesplitst.eigen;
  const citaat = alleenCitaat ? '' : gesplitst.citaat;
  const eerder = (citaat.match(/^\s*(van|from):/gim) || []).length || (citaat ? 1 : 0);
  return (
    <Box sx={{ mt: 1.5 }}>
      {(eigen || !doorgestuurd) && (
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.65, maxWidth: 760 }}>
          {metLinks(eigen || schoonMailTekst(tekst))}
        </Typography>
      )}
      {doorgestuurd && (
        <Box sx={{ mt: eigen ? 1.5 : 0, pl: 1.5, borderLeft: `3px solid ${T.primary}33` }}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.primary, fontWeight: 700, display: 'block', mb: 0.5 }}>
            ↪ Doorgestuurd · {doorgestuurd.van}{doorgestuurd.datum ? ` · ${doorgestuurd.datum}` : ''}{doorgestuurd.onderwerp ? ` · ${doorgestuurd.onderwerp}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.text, whiteSpace: 'pre-wrap', lineHeight: 1.65, maxWidth: 760 }}>
            {metLinks(doorgestuurd.tekst)}
          </Typography>
          <Uitklap label="handtekening" tekst={doorgestuurd.handtekening} />
        </Box>
      )}
      <Uitklap label="handtekening" tekst={handtekening} />
      <Uitklap label={eerder > 1 ? `${eerder} eerdere berichten` : 'eerder bericht'} tekst={citaat} />
    </Box>
  );
}

// Losse mails van één gesprek horen bij elkaar (Joris 18 sep: "veel losse e-mails die
// eigenlijk één thread zijn"). Sleutel = conversationId; zonder id op onderwerp zonder Re:/FW:.
const zonderPrefix = (s) => String(s || '').replace(/^((re|fw|fwd|aw|antw)\s*:\s*)+/i, '').trim();
function groepeerThreads(mails) {
  const map = new Map();
  (mails || []).forEach((m) => {
    const key = m.conversationId || `onderwerp:${zonderPrefix(m.onderwerp).toLowerCase()}`;
    if (!map.has(key)) map.set(key, { key, onderwerp: zonderPrefix(m.onderwerp) || '(geen onderwerp)', berichten: [] });
    map.get(key).berichten.push(m);
  });
  return [...map.values()].map((g) => {
    g.berichten.sort((a, b) => String(b.datum || '').localeCompare(String(a.datum || '')));
    g.laatste = g.berichten[0];
    g.deelnemers = [...new Set(g.berichten.map((m) => m.van).filter(Boolean))];
    return g;
  }).sort((a, b) => String(b.laatste?.datum || '').localeCompare(String(a.laatste?.datum || '')));
}

// Eén thread: kop met aantal en deelnemers; open → de losse berichten; "Notitie" bewaart
// het HELE gesprek chronologisch als één projectnotitie (zonder handtekeningen/citaten).
function ThreadRow({ groep, projectId, projectNumber, onWeggeklikt }) {
  const [open, setOpen] = useState(false);
  const actieStijl = { display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, '&:hover': { color: T.primary, textDecoration: 'underline' } };
  return (
    <Box sx={{ bgcolor: T.surface, boxShadow: T.shadow, borderRadius: '0.75rem', border: `1px solid ${T.surfaceHigh}`, overflow: 'hidden' }}>
      <Box onClick={() => setOpen((v) => !v)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
        <Box sx={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s', display: 'flex', mt: 0.25 }}>
          <Icon name="expand_more" size={20} sx={{ color: T.outlineVariant }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {groep.onderwerp}
            <Box component="span" sx={{ ml: 1, px: 0.8, py: 0.1, borderRadius: 1, bgcolor: T.surfaceHigh, fontSize: '0.7rem', color: T.secondary, fontWeight: 700 }}>{groep.berichten.length} berichten</Box>
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Icon name="forum" size={12} /> {groep.deelnemers.join(', ')}
          </Typography>
          {!open && groep.laatste?.preview && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {groep.laatste.preview}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          {projectId && (
            <NotitieKnop groep={groep} label="Hele gesprek als notitie" titel="Bewaar het hele gesprek als één projectnotitie" stijl={actieStijl} />
          )}
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, whiteSpace: 'nowrap' }}>{formatDate(groep.laatste?.datum)}</Typography>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1, borderTop: `1px solid ${T.surfaceLow}`, pt: 1.5 }}>
          {groep.berichten.map((m) => (
            <MailRow key={m.id} mail={m} projectId={projectId} projectNumber={projectNumber} onWeggeklikt={onWeggeklikt} compact />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Correspondentie-tab op de projectpagina: alle klant-mails rond dit project,
 * gezocht in de mailbox van de ingelogde gebruiker (projectnummer/-naam + contact-e-mail).
 */
export default function ProjectCorrespondence({ active, projectId, projectNumber, projectName, contactEmail, tasks, deliverables, mailNotities, onOpenNotitie }) {
  const taken = useMemo(() => ({ tasks: tasks || [], deliverables: deliverables || [] }), [tasks, deliverables]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (projectNumber) params.set('projectNumber', projectNumber);
      if (projectName) params.set('projectName', projectName);
      if (contactEmail) params.set('contactEmail', contactEmail);
      params.set('days', '180');
      const r = await authFetch(`${BACKEND_URL}/Api/mail-assistant/project-correspondence?${params.toString()}`);
      const data = await leesJson(r, 'Correspondentie laden');
      setEmails(data?.emails || []);
      setRestricted(!!data?.restricted);
    } catch (e) {
      setError(e.message || 'Kon correspondentie niet laden.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [projectNumber, projectName, contactEmail]);

  // Lazy: pas laden wanneer de tab actief wordt (bespaart Graph-calls per project-open).
  useEffect(() => {
    if (active && !loaded && !loading) load();
  }, [active, loaded, loading, load]);

  // Bij openen de bewaarde status opnieuw ophalen (kan intussen elders zijn bijgewerkt).
  const laadNotitieStatus = mailNotities?.laad;
  useEffect(() => {
    if (active && laadNotitieStatus) laadNotitieStatus();
  }, [active, laadNotitieStatus]);
  const notitieWaarde = useMemo(() => (mailNotities ? {
    stand: mailNotities.stand, start: mailNotities.start, openNotitie: onOpenNotitie,
  } : null), [mailNotities, onOpenNotitie]);

  if (loading && !loaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Mails zoeken…</Typography>
      </Box>
    );
  }

  return (
    <ProjectTakenContext.Provider value={taken}>
    <MailNotitieContext.Provider value={notitieWaarde}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
          Suggesties uit jouw mailbox (laatste 180 dagen) — alleen jij ziet dit · gezocht op
          {projectNumber ? ` #${projectNumber}` : ''}{projectName ? ` · ${projectName}` : ''}{contactEmail ? ` · ${contactEmail}` : ''}
        </Typography>
        <Box
          component="span"
          onClick={loading ? undefined : load}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: loading ? 'default' : 'pointer',
            fontFamily: T.fontBody, fontSize: '0.78rem', color: T.primary, opacity: loading ? 0.5 : 1,
            '&:hover': { textDecoration: loading ? 'none' : 'underline' },
          }}
        >
          <Icon name="refresh" size={16} /> Vernieuwen
        </Box>
      </Box>

      {error && (
        <Typography sx={{ fontFamily: T.fontBody, color: '#a83836', mb: 2 }}>{error}</Typography>
      )}

      {restricted && emails.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Icon name="lock" size={40} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>
            Geen toegang tot mailbox-zoeken (buiten de Frits-toegangsgroep).
          </Typography>
        </Box>
      )}

      {!restricted && emails.length === 0 && loaded && !loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Icon name="forum" size={40} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline, fontWeight: 500 }}>
            Geen mails gevonden voor dit project
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, maxWidth: 420, mx: 'auto', mt: 0.5 }}>
            Er is in de laatste 180 dagen geen correspondentie gevonden op projectnummer, -naam of contactpersoon.
          </Typography>
        </Box>
      )}

      {emails.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {groepeerThreads(emails).map((g) => (
            g.berichten.length === 1
              ? <MailRow key={g.berichten[0].id} mail={g.berichten[0]} projectId={projectId} projectNumber={projectNumber}
                  onWeggeklikt={(id) => setEmails((prev) => prev.filter((x) => x.id !== id))} />
              : <ThreadRow key={g.key} groep={g} projectId={projectId} projectNumber={projectNumber}
                  onWeggeklikt={(id) => setEmails((prev) => prev.filter((x) => x.id !== id))} />
          ))}
        </Box>
      )}
    </Box>
    </MailNotitieContext.Provider>
    </ProjectTakenContext.Provider>
  );
}
